<template>
  <div class="master-report-print">
    <v-card class="master-report-controls mb-5">
      <v-card-text>
        <v-row align="end">
          <v-col
            cols="12"
            sm="6"
            lg="4"
          >
            <v-select
              v-model="selectedWarehouseId"
              :items="warehouseOptions"
              :loading="warehouseOptionsLoading"
              item-text="name"
              item-value="warehouseId"
              label="Warehouse"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="4"
          >
            <v-autocomplete
              v-model="selectedClientIds"
              :items="clientOptions"
              :loading="clientOptionsLoading"
              item-text="displayName"
              item-value="id"
              label="Clients"
              hint="Empty selection includes all clients"
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              clearable
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < visibleChipCount"
                  close
                  small
                  @click:close="removeClient(item.id)"
                >
                  {{ item.name }}
                </v-chip>
                <span
                  v-else-if="index === visibleChipCount"
                  class="grey--text text-caption"
                >
                  +{{ selectedClientIds.length - visibleChipCount }} more
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="4"
          >
            <v-autocomplete
              v-model="selectedSubClientIds"
              :items="subClientOptions"
              :loading="subClientOptionsLoading"
              :disabled="selectedClientIds.length !== 1"
              :hint="subClientHint"
              item-text="displayName"
              item-value="subClientId"
              label="Subclients"
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              clearable
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < visibleChipCount"
                  close
                  small
                  @click:close="removeSubClient(item.subClientId)"
                >
                  {{ item.name }}
                </v-chip>
                <span
                  v-else-if="index === visibleChipCount"
                  class="grey--text text-caption"
                >
                  +{{ selectedSubClientIds.length - visibleChipCount }} more
                </span>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row align="end">
          <v-col
            cols="12"
            sm="6"
            lg="3"
          >
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(startDate)"
                  label="Period start"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="3"
          >
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(endDate)"
                  :error-messages="dateError"
                  label="Period end"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            lg="2"
          >
            <v-checkbox
              v-model="showItems"
              label="Show items"
              hide-details
              class="mb-4"
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
            lg="2"
          >
            <v-checkbox
              v-model="showVolumeRate"
              label="Show volume &amp; rate"
              hide-details
              class="mb-4"
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
            lg="2"
          >
            <v-checkbox
              v-model="showWarehouseLocation"
              label="Show warehouse/location"
              hide-details
              class="mb-4"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="master-report-actions d-flex flex-wrap justify-end">
              <v-btn
                color="primary"
                :loading="reportLoading"
                :disabled="!datesValid"
                class="ma-1"
                @click="runReport"
              >
                Run
              </v-btn>
              <v-btn
                text
                color="primary"
                :disabled="!report"
                class="ma-1"
                @click="exportCsv"
              >
                Export CSV
              </v-btn>
              <v-btn
                text
                color="primary"
                :disabled="!report"
                :loading="pdfLoading"
                class="ma-1"
                @click="downloadPdf"
              >
                Download PDF
              </v-btn>
              <v-btn
                text
                color="primary"
                :disabled="!report"
                :loading="zipLoading"
                class="ma-1"
                @click="downloadSubClientPdfs"
              >
                Export Sub-Client PDFs
              </v-btn>
              <v-btn
                v-if="sendClientCount > 0"
                color="teal accent-4"
                dark
                :loading="sendRecipientsLoading"
                class="ma-1"
                @click="openSendDialog"
              >
                {{ sendButtonLabel }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <div
          v-if="filtersChanged"
          class="filter-changed text-caption grey--text text--darken-1 text-right"
        >
          Filters changed. Run the report to apply them.
        </div>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      class="no-print"
      type="error"
      dense
      dismissible
      @input="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <template v-if="report">
      <v-row class="summary-band">
        <v-col
          cols="12"
          sm="6"
          lg="3"
        >
          <v-card
            color="primary"
            dark
            height="100%"
          >
            <v-card-text class="white--text">
              <div class="text-caption">
                Total to invoice
              </div>
              <div class="text-h5 font-weight-bold">
                {{ report.totals.periodTotal | currency }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          v-for="card in summaryCards"
          :key="card.label"
          cols="6"
          sm="3"
          lg="2"
        >
          <v-card height="100%">
            <v-card-text>
              <div class="text-caption grey--text text--darken-1">
                {{ card.label }}
              </div>
              <div class="text-h5">
                {{ numberFormat(card.value) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-alert
        dense
        text
        color="amber darken-3"
        class="report-callout mt-4 mb-4"
      >
        This report shows charges accrued in the selected period — not outstanding balance.
      </v-alert>

      <v-card class="report-table-card">
        <v-card-title class="master-report-table-title py-2">
          <span class="text-subtitle-1">Master Report</span>
          <v-spacer />
          <div class="master-report-actions">
            <v-btn
              text
              x-small
              color="primary"
              @click="expandAll"
            >
              Expand all
            </v-btn>
            <v-btn
              text
              x-small
              color="primary"
              @click="collapseAll"
            >
              Collapse all
            </v-btn>
          </div>
        </v-card-title>
        <v-simple-table class="master-report-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Name</th>
                <th v-if="lastRun.showWarehouseLocation">
                  Warehouse / Location
                </th>
                <th
                  class="text-right sortable-column"
                  :aria-sort="ariaSort('items')"
                  tabindex="0"
                  @click="cycleSort('items')"
                  @keydown.enter.prevent="cycleSort('items')"
                  @keydown.space.prevent="cycleSort('items')"
                >
                  Items
                  <v-icon small>
                    {{ sortIcon('items') }}
                  </v-icon>
                </th>
                <th
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                >
                  Volume
                </th>
                <th
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                >
                  Rate
                </th>
                <th class="text-right">
                  Storage days
                </th>
                <th class="text-right">
                  Storage charge
                </th>
                <th class="text-right">
                  Additional services
                </th>
                <th
                  class="text-right sortable-column"
                  :aria-sort="ariaSort('total')"
                  tabindex="0"
                  @click="cycleSort('total')"
                  @keydown.enter.prevent="cycleSort('total')"
                  @keydown.space.prevent="cycleSort('total')"
                >
                  Period total
                  <v-icon small>
                    {{ sortIcon('total') }}
                  </v-icon>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in flattenedRows"
                :key="row.key"
                :class="rowClasses(row)"
                @click="toggleRow(row)"
              >
                <td :style="nameCellStyle(row)">
                  <span class="row-twisty">
                    <v-icon
                      v-if="row.expandable"
                      small
                      color="primary"
                    >
                      {{ isOpen(row.key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </span>
                  <a
                    v-if="row.type === 'item'"
                    :href="$router.resolve({
                      name: 'inventoryEdit', params: { itemId: row.itemId },
                    }).href"
                    target="_blank"
                    rel="noopener"
                    class="item-link"
                    @click.stop
                  >{{ row.name }}</a>
                  <span v-else>{{ row.name }}</span>
                  <v-icon
                    v-if="row.type === 'client' || (row.type === 'subclient' && row.subClientId)"
                    x-small
                    class="drill-icon ml-1"
                    :title="drillLabel(row)"
                    :aria-label="drillLabel(row)"
                    @click.stop="openDrillDown(row)"
                  >
                    mdi-filter-variant
                  </v-icon>
                  <v-chip
                    v-if="row.deliveredDate"
                    x-small
                    class="ml-2"
                  >
                    delivered {{ row.deliveredDate }}
                  </v-chip>
                  <v-chip
                    v-if="row.chargesBeginDate"
                    x-small
                    class="ml-2"
                  >
                    free until {{ freeUntilDate(row.chargesBeginDate) }}
                  </v-chip>
                </td>
                <td v-if="lastRun.showWarehouseLocation">
                  {{ row.type === 'item'
                    ? [row.warehouseName, row.locationName].filter(Boolean).join(' · ') : '' }}
                </td>
                <td class="text-right">
                  {{ row.itemCount === null ? '—' : numberFormat(row.itemCount) }}
                </td>
                <td
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                >
                  {{ volumeFormat(row.volumeCubicFeet) }}
                </td>
                <td
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                >
                  {{ row.effectiveDailyRate === null
                    ? '' : dailyRateFormat(row.effectiveDailyRate) }}
                </td>
                <td class="text-right">
                  {{ numberFormat(row.storageDays) }}
                </td>
                <td class="text-right">
                  {{ row.storageCharge | currency }}
                </td>
                <td class="text-right">
                  {{ row.additionalServices | currency }}
                </td>
                <td class="text-right">
                  {{ row.periodTotal | currency }}
                </td>
              </tr>
              <tr class="grand-total-row">
                <td>{{ reportScopeName }} — total to invoice</td>
                <td v-if="lastRun.showWarehouseLocation" />
                <td class="text-right">
                  {{ numberFormat(report.totals.itemCount) }}
                </td>
                <td
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                >
                  {{ volumeFormat(report.totals.volumeCubicFeet) }}
                </td>
                <td
                  v-if="lastRun.showVolumeRate"
                  class="text-right"
                />
                <td class="text-right">
                  {{ numberFormat(report.totals.storageDays) }}
                </td>
                <td class="text-right">
                  {{ report.totals.storageCharge | currency }}
                </td>
                <td class="text-right">
                  {{ report.totals.additionalServices | currency }}
                </td>
                <td class="text-right">
                  {{ report.totals.periodTotal | currency }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </template>

    <v-dialog
      v-model="sendDialog"
      max-width="560"
    >
      <v-card>
        <v-card-title>{{ sendDialogTitle }}</v-card-title>
        <v-card-text>
          <v-alert
            v-if="sendPreview && sendPreview.testMode"
            dense
            text
            type="info"
          >
            {{ sendTestHint }}
          </v-alert>
          <p>A PDF of this report for the selected period will be emailed to:</p>
          <v-list dense>
            <v-list-item
              v-for="client in sendRecipients"
              :key="client.id"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ client.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ client.addresses.join(', ') || 'No recipient address on file' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p class="mb-0 grey--text text--darken-1">
            Each client receives only their own charges.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="sendLoading"
            @click="sendDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="teal accent-4"
            :loading="sendLoading"
            :disabled="sendLoading"
            @click="confirmSend"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="8000"
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          color="pink"
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { pageQueryUrl, readPageQuery, replacePageQuery } from '../utils/pageQuery';

const ALL_WAREHOUSES_ID = 'ALL';
const ALL_WAREHOUSES = {
  warehouseId: ALL_WAREHOUSES_ID,
  name: 'All warehouses',
};
const MASTER_QUERY_KEYS = [
  'warehouse', 'clients', 'subclients', 'start', 'end', 'items', 'volume', 'location', 'sort', 'dir',
];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function queryIds(value) {
  return typeof value === 'string'
    ? [...new Set(value.split(',').filter((id) => UUID_PATTERN.test(id)))]
    : [];
}

function validQueryDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function localDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function previousMonthPeriod() {
  const now = new Date();
  return {
    start: localDateString(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    end: localDateString(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
}

export default {
  name: 'MasterReportComponent',
  data() {
    const period = previousMonthPeriod();
    return {
      visibleChipCount: 3,
      warehouseOptions: [ALL_WAREHOUSES],
      warehouseOptionsLoading: false,
      selectedWarehouseId: ALL_WAREHOUSES_ID,
      clientOptions: [],
      clientOptionsLoading: false,
      selectedClientIds: [],
      subClientOptions: [],
      subClientOptionsLoading: false,
      selectedSubClientIds: [],
      startDate: period.start,
      endDate: period.end,
      startDateMenu: false,
      endDateMenu: false,
      showItems: false,
      showVolumeRate: false,
      showWarehouseLocation: false,
      restoringFilters: false,
      sortColumn: null,
      sortDirection: null,
      report: null,
      reportLoading: false,
      pdfLoading: false,
      zipLoading: false,
      lastRun: null,
      openRows: {},
      errorMessage: '',
      sendDialog: false,
      sendRecipientsLoading: false,
      sendLoading: false,
      sendPreview: null,
      sendRecipients: [],
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
    };
  },
  computed: {
    datesValid() {
      return Boolean(this.startDate && this.endDate && this.startDate <= this.endDate);
    },
    dateError() {
      return this.datesValid ? [] : ['Period end must be on or after period start'];
    },
    subClientHint() {
      return this.selectedClientIds.length === 1
        ? 'Empty selection includes all subclients'
        : 'Select a single client first';
    },
    currentFilterSignature() {
      return JSON.stringify({
        warehouseId: this.selectedWarehouseId,
        clientIds: [...this.selectedClientIds].sort(),
        subClientIds: [...this.selectedSubClientIds].sort(),
        startDate: this.startDate,
        endDate: this.endDate,
        includeItems: this.showItems,
        showVolumeRate: this.showVolumeRate,
        showWarehouseLocation: this.showWarehouseLocation,
      });
    },
    filtersChanged() {
      return Boolean(this.lastRun
        && this.currentFilterSignature !== this.lastRun.filterSignature);
    },
    summaryCards() {
      return [
        { label: 'Clients', value: this.report.counts.clients },
        { label: 'Subclients', value: this.report.counts.subClients },
        { label: 'Items', value: this.report.counts.items },
        { label: 'Storage days in period', value: this.report.totals.storageDays },
      ];
    },
    reportScopeName() {
      return this.lastRun ? this.lastRun.warehouseName : 'All warehouses';
    },
    sendClientCount() {
      return this.lastRun ? this.lastRun.clients.length : 0;
    },
    sendButtonLabel() {
      return this.sendClientCount === 1
        ? 'Send to Client (PDF)'
        : 'Send to Clients (PDF)';
    },
    sendDialogTitle() {
      return this.sendClientCount === 1
        ? 'Send report to client'
        : `Send reports to ${this.sendClientCount} clients`;
    },
    sendTestHint() {
      if (!this.sendPreview || !this.sendPreview.testMode) {
        return '';
      }
      return this.sendPreview.testAddress
        ? `Test mode — all reports send to ${this.sendPreview.testAddress}`
        : 'Test mode — no recipient address is configured';
    },
    flattenedRows() {
      if (!this.report || !this.lastRun) {
        return [];
      }

      const rows = [];
      const showWarehouseRows = this.lastRun.warehouseId === null;
      this.sortedSiblings(this.report.warehouses).forEach((warehouse) => {
        const warehouseKey = `warehouse:${warehouse.warehouseId}`;
        if (showWarehouseRows) {
          rows.push(this.summaryRow(
            warehouseKey,
            'warehouse',
            0,
            warehouse.name,
            warehouse,
            warehouse.clients.length > 0,
          ));
        }

        if (!showWarehouseRows || this.isOpen(warehouseKey)) {
          this.appendClientRows(rows, warehouse, showWarehouseRows ? 1 : 0);
        }
      });
      return rows;
    },
  },
  watch: {
    selectedWarehouseId() {
      if (this.restoringFilters) return;
      this.selectedClientIds = [];
      this.selectedSubClientIds = [];
      this.subClientOptions = [];
      this.loadClientOptions();
    },
    selectedClientIds() {
      if (this.restoringFilters) return;
      this.selectedSubClientIds = [];
      this.subClientOptions = [];
      if (this.selectedClientIds.length === 1) {
        this.loadSubClientOptions(this.selectedClientIds[0]);
      }
    },
  },
  async created() {
    const { query } = this.$route;
    const restore = MASTER_QUERY_KEYS.some((key) => key !== 'dir'
      && Object.prototype.hasOwnProperty.call(query, key));
    if (restore) {
      this.restoringFilters = true;
      this.selectedWarehouseId = typeof query.warehouse === 'string'
        && UUID_PATTERN.test(query.warehouse) ? query.warehouse : ALL_WAREHOUSES_ID;
      this.selectedClientIds = queryIds(query.clients);
      this.selectedSubClientIds = this.selectedClientIds.length === 1
        ? queryIds(query.subclients) : [];
      if (validQueryDate(query.start)) this.startDate = query.start;
      if (validQueryDate(query.end)) this.endDate = query.end;
      this.showItems = query.items === '1';
      this.showVolumeRate = query.volume === '1';
      this.showWarehouseLocation = query.location === '1';
      if (['items', 'total'].includes(query.sort)) {
        this.sortColumn = query.sort;
        this.sortDirection = query.dir === 'asc' ? 'asc' : 'desc';
      }
      // Let both dependent-selection watchers settle without clearing restored ids.
      await this.$nextTick();
      this.restoringFilters = false;
    }
    await Promise.all([
      this.loadWarehouseOptions(),
      this.loadClientOptions(),
      ...(this.selectedClientIds.length === 1
        ? [this.loadSubClientOptions(this.selectedClientIds[0])] : []),
    ]);
    if (restore) await this.runReport();
  },
  methods: {
    ...mapActions('apiClient', ['get', 'post']),
    reportQuery(filters) {
      const query = readPageQuery();
      MASTER_QUERY_KEYS.forEach((key) => { delete query[key]; });
      if (filters.warehouseId) query.warehouse = filters.warehouseId;
      if (filters.clientIds.length) query.clients = filters.clientIds.join(',');
      if (filters.subClientIds.length) query.subclients = filters.subClientIds.join(',');
      // Dates stay explicit so shared links retain their period as defaults advance.
      query.start = filters.startDate;
      query.end = filters.endDate;
      if (filters.includeItems) query.items = '1';
      if (filters.showVolumeRate) query.volume = '1';
      if (filters.showWarehouseLocation) query.location = '1';
      if (this.sortColumn) {
        query.sort = this.sortColumn;
        query.dir = this.sortDirection;
      }
      return query;
    },
    replaceReportQuery(filters) {
      replacePageQuery(this.reportQuery(filters));
    },
    drillLabel(row) {
      return row.type === 'client'
        ? 'Open report filtered to this client'
        : 'Open report filtered to this sub-client';
    },
    openDrillDown(row) {
      const query = this.reportQuery({
        ...this.lastRun,
        clientIds: [row.clientId],
        subClientIds: row.type === 'subclient' ? [row.subClientId] : [],
      });
      window.open(pageQueryUrl(query), '_blank', 'noopener');
    },
    cycleSort(column) {
      if (this.sortColumn !== column) {
        this.sortColumn = column;
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortDirection = 'asc';
      } else {
        this.sortColumn = null;
        this.sortDirection = null;
      }
      if (this.lastRun) this.replaceReportQuery(this.lastRun);
    },
    ariaSort(column) {
      if (this.sortColumn !== column) return 'none';
      return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },
    sortIcon(column) {
      if (this.sortColumn !== column) return 'mdi-unfold-more-horizontal';
      return this.sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },
    sortedSiblings(siblings) {
      if (!this.sortColumn) return siblings;
      const field = this.sortColumn === 'items' ? 'itemCount' : 'periodTotal';
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      return siblings.map((row, index) => ({ row, index }))
        .sort((a, b) => direction * (Number(a.row[field] || 0) - Number(b.row[field] || 0))
          || a.index - b.index)
        .map(({ row }) => row);
    },
    async loadWarehouseOptions() {
      this.warehouseOptionsLoading = true;
      try {
        const response = await this.get({
          url: '/warehouses',
          params: { activeOnly: true },
        });
        this.warehouseOptions = [ALL_WAREHOUSES, ...response.data];
      } catch (error) {
        this.errorMessage = 'Warehouse options could not be loaded.';
      } finally {
        this.warehouseOptionsLoading = false;
      }
    },
    async loadClientOptions() {
      this.clientOptionsLoading = true;
      try {
        const params = this.selectedWarehouseId !== ALL_WAREHOUSES_ID
          ? { warehouseId: this.selectedWarehouseId }
          : {};
        const response = await this.get({
          url: '/reports/master/clients',
          params,
        });
        this.clientOptions = response.data.map((client) => ({
          ...client,
          displayName: client.icnAbbreviation
            ? `${client.name} (${client.icnAbbreviation})`
            : client.name,
        }));
      } catch (error) {
        this.clientOptions = [];
        this.errorMessage = 'Client options could not be loaded.';
      } finally {
        this.clientOptionsLoading = false;
      }
    },
    async loadSubClientOptions(clientId) {
      this.subClientOptionsLoading = true;
      try {
        const response = await this.get({
          url: `/clients/${clientId}/subClients/options`,
        });
        this.subClientOptions = response.data.map((client) => ({
          ...client,
          displayName: client.icnAbbreviation
            ? `${client.name} (${client.icnAbbreviation})`
            : client.name,
        }));
      } catch (error) {
        this.subClientOptions = [];
        this.errorMessage = 'Subclient options could not be loaded.';
      } finally {
        this.subClientOptionsLoading = false;
      }
    },
    removeClient(clientId) {
      this.selectedClientIds = this.selectedClientIds.filter((id) => id !== clientId);
    },
    removeSubClient(subClientId) {
      this.selectedSubClientIds = this.selectedSubClientIds
        .filter((id) => id !== subClientId);
    },
    formatDate(date) {
      if (!date) {
        return '';
      }
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },
    numberFormat(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    volumeFormat(value) {
      return `${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} cu ft`;
    },
    dailyRateFormat(value) {
      return `$${Number(value || 0).toFixed(2)}/day`;
    },
    async runReport() {
      if (!this.datesValid) {
        return;
      }

      this.reportLoading = true;
      this.errorMessage = '';
      const warehouse = this.warehouseOptions
        .find((option) => option.warehouseId === this.selectedWarehouseId);
      const fallbackWarehouseName = this.selectedWarehouseId === ALL_WAREHOUSES_ID
        ? 'All warehouses' : 'Selected warehouse';
      const filterSignature = this.currentFilterSignature;
      const request = {
        warehouseId: this.selectedWarehouseId === ALL_WAREHOUSES_ID
          ? null
          : this.selectedWarehouseId,
        clientIds: [...this.selectedClientIds],
        subClientIds: this.selectedClientIds.length === 1
          ? [...this.selectedSubClientIds]
          : [],
        startDate: this.startDate,
        endDate: this.endDate,
        includeItems: this.showItems,
        showVolumeRate: this.showVolumeRate,
        showWarehouseLocation: this.showWarehouseLocation,
      };

      this.replaceReportQuery(request);
      try {
        const response = await this.post({
          url: '/reports/master',
          body: request,
        });
        this.report = response.data;
        this.lastRun = {
          ...request,
          clients: this.reportClients(response.data),
          warehouseName: warehouse ? warehouse.name : fallbackWarehouseName,
          filterSignature,
        };
        this.collapseAll();
      } catch (error) {
        this.errorMessage = this.apiErrorMessage(error);
      } finally {
        this.reportLoading = false;
      }
    },
    apiErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return 'The master report could not be loaded.';
    },
    summaryRow(key, type, level, name, summary, expandable, ids = {}) {
      return {
        ...ids,
        key,
        type,
        level,
        name,
        expandable,
        itemCount: summary.itemCount,
        volumeCubicFeet: summary.volumeCubicFeet,
        effectiveDailyRate: null,
        storageDays: summary.storageDays,
        storageCharge: summary.storageCharge,
        additionalServices: summary.additionalServices,
        periodTotal: summary.periodTotal,
        deliveredDate: null,
        chargesBeginDate: null,
      };
    },
    appendClientRows(rows, warehouse, level) {
      this.sortedSiblings(warehouse.clients).forEach((client) => {
        const clientKey = `client:${warehouse.warehouseId}:${client.clientId}`;
        rows.push(this.summaryRow(
          clientKey,
          'client',
          level,
          client.name,
          client,
          client.subClients.length > 0,
          { clientId: client.clientId },
        ));
        if (this.isOpen(clientKey)) {
          this.appendSubClientRows(rows, warehouse, client, level + 1);
        }
      });
    },
    appendSubClientRows(rows, warehouse, client, level) {
      this.sortedSiblings(client.subClients).forEach((subClient) => {
        const subClientKey = this.subClientKey(warehouse, client, subClient);
        const hasItems = Boolean(this.lastRun.includeItems
          && subClient.items
          && subClient.items.length);
        rows.push(this.summaryRow(
          subClientKey,
          'subclient',
          level,
          subClient.name,
          subClient,
          hasItems,
          { clientId: client.clientId, subClientId: subClient.subClientId },
        ));

        if (hasItems && this.isOpen(subClientKey)) {
          this.sortedSiblings(subClient.items).forEach((item) => {
            rows.push({
              itemId: item.itemId,
              key: `item:${warehouse.warehouseId}:${item.itemId}`,
              type: 'item',
              level: level + 1,
              name: item.description ? `${item.icn} — ${item.description}` : item.icn,
              expandable: false,
              itemCount: null,
              volumeCubicFeet: item.volumeCubicFeet,
              effectiveDailyRate: item.effectiveDailyRate,
              storageDays: item.storageDays,
              storageCharge: item.storageCharge,
              additionalServices: item.additionalServices,
              periodTotal: item.periodTotal,
              deliveredDate: item.deliveredDate,
              chargesBeginDate: item.chargesBeginDate,
              warehouseName: item.warehouseName,
              locationName: item.locationName,
            });
          });
        }
      });
    },
    subClientKey(warehouse, client, subClient) {
      const id = subClient.subClientId || 'none';
      return `subclient:${warehouse.warehouseId}:${client.clientId}:${id}`;
    },
    isOpen(key) {
      return Boolean(this.openRows[key]);
    },
    toggleRow(row) {
      if (!row.expandable) {
        return;
      }
      this.$set(this.openRows, row.key, !this.isOpen(row.key));
    },
    expandAll() {
      const openRows = {};
      if (!this.report || !this.lastRun) {
        this.openRows = openRows;
        return;
      }

      this.report.warehouses.forEach((warehouse) => {
        if (this.lastRun.warehouseId === null && warehouse.clients.length) {
          openRows[`warehouse:${warehouse.warehouseId}`] = true;
        }
        warehouse.clients.forEach((client) => {
          if (client.subClients.length) {
            openRows[`client:${warehouse.warehouseId}:${client.clientId}`] = true;
          }
          if (this.lastRun.includeItems) {
            client.subClients.forEach((subClient) => {
              if (subClient.items && subClient.items.length) {
                openRows[this.subClientKey(warehouse, client, subClient)] = true;
              }
            });
          }
        });
      });
      this.openRows = openRows;
    },
    collapseAll() {
      this.openRows = {};
    },
    rowClasses(row) {
      return {
        'warehouse-row': row.type === 'warehouse',
        'client-row': row.type === 'client',
        'item-row': row.type === 'item',
        'expandable-row': row.expandable,
        'font-weight-bold': row.expandable && this.isOpen(row.key),
      };
    },
    nameCellStyle(row) {
      return { paddingLeft: `${16 + row.level * 24}px` };
    },
    reportClients(report) {
      const clients = {};
      report.warehouses.forEach((warehouse) => {
        warehouse.clients.forEach((client) => {
          clients[client.clientId] = {
            id: client.clientId,
            name: client.name,
          };
        });
      });
      return Object.values(clients);
    },
    lastRunRequest(clientIds = null) {
      return {
        warehouseId: this.lastRun.warehouseId,
        clientIds: clientIds || [...this.lastRun.clientIds],
        subClientIds: [...this.lastRun.subClientIds],
        startDate: this.lastRun.startDate,
        endDate: this.lastRun.endDate,
        includeItems: this.lastRun.includeItems,
        showVolumeRate: this.lastRun.showVolumeRate,
        showWarehouseLocation: this.lastRun.showWarehouseLocation,
      };
    },
    async downloadPdf() {
      if (!this.report || !this.lastRun) {
        return;
      }

      this.pdfLoading = true;
      this.errorMessage = '';
      try {
        const response = await this.post({
          url: '/reports/master/download',
          body: this.lastRunRequest(),
        });
        const pdf = this.base64ToArrayBuffer(response.data);
        this.createAndDownloadBlobFile(
          pdf,
          `master-report-${this.lastRun.startDate}-to-${this.lastRun.endDate}.pdf`,
          'application/pdf',
        );
      } catch (error) {
        this.errorMessage = 'The master report PDF could not be downloaded.';
      } finally {
        this.pdfLoading = false;
      }
    },
    async downloadSubClientPdfs() {
      if (!this.report || !this.lastRun) {
        return;
      }

      this.zipLoading = true;
      this.errorMessage = '';
      const request = this.lastRunRequest();
      try {
        const response = await this.post({
          url: '/reports/master/batch-download',
          body: request,
        });
        const zip = this.base64ToArrayBuffer(response.data);
        this.createAndDownloadBlobFile(
          zip,
          `sub-client-reports-${request.startDate}-to-${request.endDate}.zip`,
          'application/zip',
        );
      } catch (error) {
        const response = error && (error.response || error);
        const body = response && response.data;
        const message = typeof body === 'string' ? body : body && body.message;
        this.errorMessage = response && response.status === 400
          && typeof message === 'string' && message.trim()
          ? message : 'The sub-client PDF export could not be generated.';
      } finally {
        this.zipLoading = false;
      }
    },
    base64ToArrayBuffer(data) {
      const binary = window.atob(data);
      const bytes = new Uint8Array(binary.length);
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
      }
      return bytes;
    },
    exportCsv() {
      if (!this.report || !this.lastRun) {
        return;
      }

      const header = [
        'level',
        'warehouse',
        'client',
        'subclient',
        'icn',
        'description',
        'items',
      ];
      if (this.lastRun.showVolumeRate) {
        header.push('volume_cuft', 'daily_rate');
      }
      header.push(
        'storage_days',
        'storage_charge',
        'additional_services',
        'period_total',
        'Free until',
      );
      if (this.lastRun.showWarehouseLocation) {
        header.splice(2, 0, 'location');
      }
      const rows = [header];

      this.report.warehouses.forEach((warehouse) => {
        warehouse.clients.forEach((client) => {
          client.subClients.forEach((subClient) => {
            rows.push(this.csvSummaryRow(
              'subclient', warehouse.name, client.name, subClient.name, subClient,
            ));
            if (this.lastRun.includeItems && subClient.items) {
              subClient.items.forEach((item) => {
                const itemRow = [
                  'item',
                  warehouse.name,
                  client.name,
                  subClient.name,
                  item.icn,
                  item.description,
                  1,
                ];
                if (this.lastRun.showVolumeRate) {
                  itemRow.push(
                    this.volumeValue(item.volumeCubicFeet),
                    item.effectiveDailyRate === null ? '' : this.moneyValue(item.effectiveDailyRate),
                  );
                }
                itemRow.push(
                  item.storageDays,
                  this.moneyValue(item.storageCharge),
                  this.moneyValue(item.additionalServices),
                  this.moneyValue(item.periodTotal),
                  this.freeUntilDate(item.chargesBeginDate),
                );
                if (this.lastRun.showWarehouseLocation) {
                  itemRow.splice(2, 0, item.locationName);
                }
                rows.push(itemRow);
              });
            }
          });
          rows.push(this.csvSummaryRow(
            'client_total', warehouse.name, client.name, '', client,
          ));
        });
        rows.push(this.csvSummaryRow(
          'warehouse_total', warehouse.name, '', '', warehouse,
        ));
      });
      rows.push(this.csvSummaryRow(
        'grand_total', this.reportScopeName, '', '', this.report.totals,
      ));

      const csv = rows
        .map((row) => row.map(this.csvValue).join(','))
        .join('\r\n');
      this.createAndDownloadBlobFile(
        csv,
        `master-report-${this.lastRun.startDate}-to-${this.lastRun.endDate}.csv`,
        'text/csv;charset=utf-8',
      );
    },
    csvSummaryRow(level, warehouse, client, subClient, summary) {
      const row = [
        level,
        warehouse,
        client,
        subClient,
        '',
        '',
        summary.itemCount,
      ];
      if (this.lastRun.showVolumeRate) {
        row.push(this.volumeValue(summary.volumeCubicFeet), '');
      }
      row.push(
        summary.storageDays,
        this.moneyValue(summary.storageCharge),
        this.moneyValue(summary.additionalServices),
        this.moneyValue(summary.periodTotal),
        '',
      );
      if (this.lastRun.showWarehouseLocation) {
        row.splice(2, 0, '');
      }
      return row;
    },
    freeUntilDate(chargesBeginDate) {
      if (!chargesBeginDate) {
        return '';
      }
      const date = new Date(`${chargesBeginDate}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() - 1);
      return date.toISOString().slice(0, 10);
    },
    volumeValue(value) {
      return Number(value || 0).toFixed(1);
    },
    moneyValue(value) {
      return Number(value || 0).toFixed(2);
    },
    csvValue(value) {
      const text = value === null || value === undefined ? '' : String(value);
      if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    },
    createAndDownloadBlobFile(body, filename, contentType) {
      const blob = new Blob([body], { type: contentType });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async openSendDialog() {
      if (!this.lastRun || !this.lastRun.clients.length) {
        return;
      }

      this.sendRecipientsLoading = true;
      this.errorMessage = '';
      this.sendPreview = null;
      this.sendRecipients = [];
      try {
        const clientIds = this.lastRun.clients.map((client) => client.id);
        const response = await this.get({
          url: `/reports/master/send/preview?clientIds=${clientIds
            .map((id) => encodeURIComponent(id)).join(',')}`,
        });
        this.sendPreview = response.data;
        this.sendRecipients = response.data.results.map((result) => ({
          id: result.clientId,
          name: result.name,
          addresses: result.sentTo,
          status: result.status,
          error: result.error,
        }));
        this.sendDialog = true;
      } catch (error) {
        this.errorMessage = 'Effective report recipients could not be loaded.';
      } finally {
        this.sendRecipientsLoading = false;
      }
    },
    async confirmSend() {
      if (!this.lastRun || this.sendLoading) {
        return;
      }

      this.sendLoading = true;
      this.errorMessage = '';
      try {
        const clientIds = this.lastRun.clients.map((client) => client.id);
        const response = await this.post({
          url: '/reports/master/send',
          body: this.lastRunRequest(clientIds),
        });
        const results = response.data;
        const sentCount = results.filter((result) => result.status === 'sent').length;
        const notSent = results.filter((result) => result.status !== 'sent');
        const sentText = sentCount === 1 ? 'Sent 1 report' : `Sent ${sentCount} reports`;
        if (notSent.length) {
          const details = notSent.map((result) => {
            const reason = result.status === 'skipped_no_recipient'
              ? 'no recipient address'
              : (result.error || 'send failed');
            return `${result.name}: ${reason}`;
          }).join('; ');
          this.snackbarMessage = `${sentText}. Not sent — ${details}`;
          this.snackbarColor = sentCount ? 'warning' : 'error';
        } else {
          this.snackbarMessage = sentText;
          this.snackbarColor = 'success';
        }
        this.sendDialog = false;
        this.snackbar = true;
      } catch (error) {
        this.errorMessage = 'The reports could not be sent.';
      } finally {
        this.sendLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.master-report-actions {
  gap: 4px;
}

.report-table-card {
  overflow-x: auto;
}

.master-report-table {
  min-width: 900px;
}

.master-report-table tbody tr.expandable-row {
  cursor: pointer;
}

.master-report-table tbody tr.warehouse-row {
  background: #eceff1;
}

.master-report-table tbody tr.client-row {
  background: #f5f5f5;
}

.master-report-table tbody tr.item-row td {
  color: #424242;
}

.row-twisty {
  display: inline-block;
  width: 24px;
}

.sortable-column {
  cursor: pointer;
}

.drill-icon {
  opacity: 0;
  background: #1976d2;
  color: #fff !important;
  border-radius: 50%;
  padding: 3px;
}

tr:hover .drill-icon,
.drill-icon:focus {
  opacity: 1;
}

/* new Vuetify({}) does not emit --v-* custom properties; use the literal primary. */
.master-report-table .item-link,
.master-report-table .item-link:visited {
  color: #1976d2;
  font: inherit;
  text-decoration: none;
}

.master-report-table .item-link:hover {
  text-decoration: underline;
}

.grand-total-row td {
  position: sticky;
  bottom: 0;
  background: #1976d2;
  color: white;
  font-weight: 500;
}
</style>

<style>
@media print {
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-tabs-bar,
  .master-report-controls,
  .master-report-actions,
  .filter-changed,
  .no-print {
    display: none !important;
  }

  .v-main {
    padding: 0 !important;
  }

  .v-main__wrap > .container,
  .master-report-print {
    max-width: none !important;
    padding: 0 !important;
  }

  .summary-band .v-card,
  .report-table-card {
    box-shadow: none !important;
  }

  .report-table-card {
    overflow: visible !important;
  }

  .master-report-table {
    min-width: 0 !important;
  }

  .master-report-table table {
    font-size: 10px;
  }

  .master-report-table th,
  .master-report-table td {
    padding: 0 6px !important;
  }

  .grand-total-row td {
    position: static !important;
    print-color-adjust: exact;
  }
}
</style>
